import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import * as R from 'ramda';

import { useGoogleMapsStateValue } from '@atom/components/common/map/hooks/googleMapsStateHook';
import Map from '@atom/components/common/map/Map';
import MapMarkers from '@atom/components/common/map/MapMarkers';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import { getLoggedInUserMapLocationSelector } from '@atom/selectors/mapSelectors';
import { MapParams } from '@atom/types/map';
import {
  getMapShapeBoundingBox,
  shapeIsPolyline,
} from '@atom/utilities/mapShapeUtilities';
import {
  buildMapDataRequest,
  getGeoJsonFromMapBounds,
  getGrabShape,
  getMapCenterAndZoomParams,
  urlValueToLatLng,
} from '@atom/utilities/mapUtilities';

import Kml from '../common/map/kml/Kml';
import MapPinMarker from '../common/map/markers/MapPinMarker';

import { useLayersValue } from './hooks/layersHook';
import { useListView } from './hooks/listViewHook';
import { useMapData } from './hooks/mapDataHook';
import MapStats from './mapStats/MapStats';

const MapContainer = () => {
  const [map, setMap] = useState<google.maps.Map>(null);
  const [coordinates, setCoordinates] = useState(null);
  const { grab } = useGoogleMapsStateValue();

  const preferences = usePreferences();
  const currentUserLocation = useSelector(getLoggedInUserMapLocationSelector);

  const [{ center, zoom, searchPoint }, { updateSearchParams }] = useNavigation(
    {
      center: SearchParamType.STRING,
      zoom: SearchParamType.NUMBER,
      searchPoint: SearchParamType.STRING,
      id: SearchParamType.STRING,
      view: SearchParamType.STRING,
      hover: SearchParamType.STRING,
    },
  );

  const [, setListView] = useListView();

  const layers = useLayersValue();
  const { shape } = useGoogleMapsStateValue();

  const useShapeCoordinates = shape && !shapeIsPolyline(shape);

  const onLoad = useCallback((gmap: google.maps.Map) => {
    setMap(gmap);
  }, []);

  const onIdle = useCallback(() => {
    try {
      const existingMapParams: MapParams = {
        center,
        zoom,
      };

      const mapParams = getMapCenterAndZoomParams(
        map,
        existingMapParams,
        preferences,
        currentUserLocation,
      );

      if (!R.equals(existingMapParams, mapParams) || !coordinates) {
        updateSearchParams({ zoom: mapParams.zoom, center: mapParams.center });
        R.pipe(
          getGeoJsonFromMapBounds,
          R.prop('coordinates'),
          JSON.stringify,
          setCoordinates,
        )(map);
      }
    } catch {
      updateSearchParams({ zoom: null, center: null });
    }
  }, [
    map,
    center,
    zoom,
    searchPoint,
    coordinates,
    preferences,
    updateSearchParams,
  ]);

  const url = buildMapDataRequest(layers, {
    coordinates: useShapeCoordinates
      ? JSON.stringify(getMapShapeBoundingBox(shape))
      : coordinates,
    zoom,
  });

  const [mapData, loading] = useMapData(url);

  const grabInRadius = async event => {
    const id = getGrabShape(map, mapData)(event);
    if (grab) {
      updateSearchParams({ id, view: 'list', hover: null });
    }
  };

  useEffect(() => {
    setListView(mapData);
  }, [mapData]);

  return (
    <Map
      onIdle={onIdle}
      onLoad={onLoad}
      center={center && urlValueToLatLng(center)}
      zoom={zoom}
      loading={loading}
      mapStyleControl
      mapSearchBox
      layerControls
      drawingControls
      onClick={grabInRadius}
    >
      <MapMarkers data={mapData} />
      {searchPoint && <MapPinMarker center={searchPoint} />}
      <MapStats data={mapData} loading={loading} />
      {layers.kmlLayers && !!layers.kmlLayerIds.size && <Kml map={map} />}
    </Map>
  );
};

export default React.memo(MapContainer);
